import { Injectable } from "@angular/core";
import { NativeBiometric, BiometryType } from "@capgo/capacitor-native-biometric";
import { Capacitor } from "@capacitor/core";

@Injectable({ providedIn: "root" })
export class BiometricService {
    // Unique service identifier for iOS Keychain item; also used on Android Keystore label.
    private readonly serverId = "dk.dffedb.eforsyning";

    async isAvailable(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) return false;
        try {
            const { isAvailable } = await NativeBiometric.isAvailable();
            return !!isAvailable;
        } catch (err) {
            console.error("NativeBiometric.isAvailable() failed:", err);
            return false;
        }
    }

    async getBiometryType(): Promise<BiometryType | null> {
        if (!Capacitor.isNativePlatform()) return null;
        try {
            const { isAvailable, biometryType } = await NativeBiometric.isAvailable();
            return isAvailable ? (biometryType ?? null) : null;
        } catch {
            return null;
        }
    }

    async enableBiometricLogin(
        userId: string,
        secret: string,
        biometricPrompt: string = "Aktiver biometrisk login",
        biometricTitle: string = "Aktiver biometrisk login",
        biometricDescription: string = "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
    ): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await NativeBiometric.verifyIdentity({
                reason: biometricPrompt,
                title: biometricTitle,
                description: biometricDescription
            });

            const username = String(userId ?? "");
            const password = String(secret ?? "");
            try {
                await NativeBiometric.setCredentials({ username, password, server: this.serverId });
            } catch (err) {
                try {
                    await NativeBiometric.deleteCredentials({ server: this.serverId });
                } catch (deleteErr) {
                    console.warn("NativeBiometric.deleteCredentials failed while recovering from set error", deleteErr);
                }
                await NativeBiometric.setCredentials({ username, password, server: this.serverId });
            }
        } catch (err) {
            console.error("Failed to store biometric credentials", err);
            throw err;
        }
    }

    async isBiometricLoginEnabled(): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) return false;
        try {
            const { username, password } = await NativeBiometric.getCredentials({ server: this.serverId });
            const enabled = !!username && !!password;
            return enabled;
        } catch (err) {
            if (!this.isNoCredentialsError(err)) {
                console.error("Biometric credentials not found", err);
            }
            return false;
        }
    }

    async authenticate(
        biometricPrompt = "Log ind med biometrisk godkendelse",
        biometricTitle = "Log ind med biometrisk godkendelse",
        biometricDescription = "Bekræft din identitet med biometrisk godkendelse for at logge ind."
    ): Promise<{ userId: string; secret: string } | null> {
        if (!Capacitor.isNativePlatform()) return null;

        await NativeBiometric.verifyIdentity({
            reason: biometricPrompt,
            title: biometricTitle,
            description: biometricDescription
        });

        const { username, password } = await NativeBiometric.getCredentials({ server: this.serverId });
        if (!username || !password) return null;

        return { userId: username, secret: password };
    }

    async getStoredCredentials(): Promise<{ userId: string; secret: string } | null> {
        if (!Capacitor.isNativePlatform()) return null;
        try {
            const { username, password } = await NativeBiometric.getCredentials({ server: this.serverId });
            if (!username || !password) return null;
            return { userId: username, secret: password };
        } catch (err) {
            return null;
        }
    }

    async getBiometricActionText(biometryType?: BiometryType | null): Promise<string> {
        const val = biometryType ?? "";

        if (typeof val === "number") {
            switch (val) {
                case 0:
                    return "Biometrisk godkendelse";
                case 1:
                case 3:
                    return "Fingeraftryk";
                case 2:
                case 4:
                    return "Face ID";
                case 5:
                    return "Iris";
                case 6:
                    return "Biometrisk godkendelse";
                default:
                    return "Biometrisk godkendelse";
            }
        }

        const biometricType = String(val).toLowerCase();
        if (!biometricType) return "Biometrisk godkendelse";

        if (biometricType.includes("face") || biometricType.includes("faceid") || biometricType.includes("face_id") || biometricType.includes("face id")) {
            return "Face ID";
        }
        if (biometricType.includes("touch") || biometricType.includes("finger") || biometricType.includes("fingerprint") || biometricType.includes("finger_print")) {
            return "Fingeraftryk";
        }
        if (biometricType.includes("iris")) {
            return "Iris";
        }
        return "Biometrisk godkendelse";
    }

    // Bruges kun til debugging indtil videre
    async disableBiometricLogin(): Promise<void> {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await NativeBiometric.deleteCredentials({ server: this.serverId });
            localStorage.removeItem("biometricsAsked");
            alert("Biometrisk login deaktiveret.");
        } catch {
            alert(
                "Fejl ved deaktivering af biometrisk login. Det kan være nødvendigt at slette appdata eller geninstallere appen for at fjerne biometriske loginoplysninger."
            );
        }
    }

    async platform(): Promise<string> {
        return Capacitor.getPlatform?.() ?? "unknown";
    }

    private isNoCredentialsError(err: unknown): boolean {
        const message = (err as { message?: string })?.message ?? String(err);
        return message.toLowerCase().includes("no credentials");
    }
}
